import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronRight, Search, UtensilsCrossed, Wine } from 'lucide-react';
import type { CategoryNode, MenuItem } from '@sebastian/shared';
import { useCurrency } from '../lib/currency';
import {
  buildCategoryColors,
  categoryColorVars,
  colorFor,
  type CategoryColor,
  type CategoryColorMap,
} from '../lib/categoryColors';
import { Badge, EmptyState, Input } from './ui';

/** Flatten a category subtree into the set of ids it covers. */
function collectIds(node: CategoryNode): string[] {
  return [node.id, ...node.children.flatMap(collectIds)];
}

function CategoryRow({
  node,
  depth,
  color,
  selectedId,
  onSelect,
}: {
  node: CategoryNode;
  depth: number;
  color: CategoryColor;
  selectedId: string | null;
  onSelect: (node: CategoryNode | null) => void;
}) {
  const [expanded, setExpanded] = useState(depth === 0);
  const hasChildren = node.children.length > 0;
  const active = selectedId === node.id;

  return (
    <div>
      <div
        className={clsx(
          'flex items-center rounded-lg transition-colors',
          !active && 'text-ink-soft hover:bg-hover hover:text-ink',
        )}
        // A selected row wears its own colour rather than the generic accent,
        // so the rail and the grid below it always agree.
        style={active ? { backgroundColor: color.tint, color: color.ink } : undefined}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={() => setExpanded((value) => !value)}
            className="flex h-11 w-7 shrink-0 items-center justify-center"
            style={{ marginLeft: depth * 10 }}
          >
            <ChevronRight size={15} className={clsx('transition-transform', expanded && 'rotate-90')} />
          </button>
        ) : (
          <span className="h-11 w-7 shrink-0" style={{ marginLeft: depth * 10 }} />
        )}
        <button
          type="button"
          onClick={() => onSelect(active ? null : node)}
          className="flex h-11 flex-1 items-center gap-2 pr-3 text-left text-sm font-medium"
        >
          {/* Children carry the same swatch as their parent, which is what
              makes the inheritance legible at a glance. */}
          <span
            aria-hidden
            className="size-2.5 shrink-0 rounded-[3px]"
            style={{ backgroundColor: color.ink }}
          />
          <span className="flex-1 truncate">{node.name}</span>
          {node.itemCount > 0 ? (
            <span className={clsx('tnum text-xs', !active && 'text-ink-faint')}>{node.itemCount}</span>
          ) : null}
        </button>
      </div>

      {hasChildren && expanded ? (
        <div>
          {node.children.map((child) => (
            <CategoryRow
              key={child.id}
              node={child}
              depth={depth + 1}
              color={color}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** An item tile, bordered in the colour of the category it belongs to. */
function ItemCard({
  item,
  colors,
  onPick,
  money,
}: {
  item: MenuItem;
  colors: CategoryColorMap;
  onPick: (item: MenuItem) => void;
  money: (cents: number) => string;
}) {
  const outOfStock = item.trackStock && (item.stockQuantity ?? 0) <= 0;

  return (
    <button
      type="button"
      disabled={outOfStock}
      onClick={() => onPick(item)}
      style={categoryColorVars(colorFor(colors, item.categoryId))}
      className={clsx(
        // border-2: at 1px the category colour read as a hairline and got
        // lost against the grid. The tile has to be identifiable by colour
        // from across the station, not on inspection.
        'cat-card flex min-h-[6.5rem] flex-col rounded-lg border-2 bg-surface p-3 text-left transition',
        outOfStock ? 'cursor-not-allowed opacity-50' : 'hover:-translate-y-0.5 active:translate-y-0',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium leading-snug text-ink">{item.name}</span>
        {item.destination === 'BAR' ? (
          <Wine size={14} className="mt-0.5 shrink-0 text-reserved" />
        ) : null}
      </div>
      {item.description ? (
        <span className="mt-1 line-clamp-2 text-[11px] leading-snug text-ink-faint">
          {item.description}
        </span>
      ) : null}
      {item.trackStock ? (
        <div className="mt-1.5">
          <Badge tone="danger">
            {outOfStock ? 'Out of stock' : `${item.stockQuantity} available`}
          </Badge>
        </div>
      ) : null}
      <div className="mt-auto flex items-end justify-between gap-2 pt-2">
        <span className="tnum text-sm font-semibold text-accent-strong">
          {money(item.priceCents)}
        </span>
        {item.modifierGroups.length > 0 ? (
          <span className="text-[10px] uppercase tracking-wider text-ink-faint">Options</span>
        ) : null}
      </div>
    </button>
  );
}

/**
 * Category rail plus item grid. Search always beats navigation for speed, so it
 * sits at the top and ignores the current category when it has a query.
 *
 * Every category carries a colour drawn from its root (see
 * `lib/categoryColors.ts`), and each item tile is bordered in the colour of
 * the category it belongs to, so the grid stays readable when "All items" puts
 * the whole menu on screen at once.
 */
export function MenuBrowser({
  categories,
  items,
  recentIds,
  onPick,
}: {
  categories: CategoryNode[];
  items: MenuItem[];
  recentIds: string[];
  onPick: (item: MenuItem) => void;
}) {
  const { money } = useCurrency();
  const [selected, setSelected] = useState<CategoryNode | null>(null);
  const [search, setSearch] = useState('');

  const colors = useMemo(() => buildCategoryColors(categories), [categories]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query) {
      return items.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.sku?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query),
      );
    }
    if (!selected) return items;
    const ids = new Set(collectIds(selected));
    return items.filter((item) => ids.has(item.categoryId));
  }, [items, search, selected]);

  const recent = useMemo(
    () =>
      recentIds
        .map((id) => items.find((item) => item.id === id))
        .filter((item): item is MenuItem => Boolean(item))
        .slice(0, 6),
    [recentIds, items],
  );

  return (
    <div className="flex min-h-0 flex-1">
      <aside className="hidden w-52 shrink-0 flex-col border-r border-line bg-surface/50 md:flex">
        <div className="border-b border-line px-3 py-3">
          <button
            type="button"
            onClick={() => setSelected(null)}
            className={clsx(
              'h-10 w-full rounded-lg px-3 text-left text-sm font-medium transition',
              selected === null ? 'bg-accent/12 text-accent-strong' : 'text-ink-soft hover:bg-hover',
            )}
          >
            All items
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {categories.map((node) => (
            <CategoryRow
              key={node.id}
              node={node}
              depth={0}
              color={colorFor(colors, node.id)}
              selectedId={selected?.id ?? null}
              onSelect={setSelected}
            />
          ))}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="border-b border-line p-3">
          <div className="relative">
            <Search
              size={17}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint"
            />
            <Input
              className="pl-10"
              placeholder="Search the menu…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          {/* Horizontal category chips stand in for the rail on narrow screens. */}
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1 md:hidden">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className={clsx(
                'h-9 shrink-0 rounded-full border px-3 text-xs font-medium',
                selected === null ? 'border-accent text-accent-strong' : 'border-line text-ink-soft',
              )}
            >
              All
            </button>
            {categories.flatMap((node) => [node, ...node.children]).map((node) => (
              <button
                key={node.id}
                type="button"
                aria-pressed={selected?.id === node.id}
                onClick={() => setSelected(node)}
                style={categoryColorVars(colorFor(colors, node.id))}
                className="cat-chip h-9 shrink-0 rounded-full border px-3 text-xs font-medium"
              >
                {node.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {recent.length > 0 && !search && !selected ? (
            <section className="mb-5">
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-faint">
                Recently ordered
              </h3>
              <div className="flex flex-wrap gap-2">
                {recent.map((item) => {
                  const soldOut = item.trackStock && (item.stockQuantity ?? 0) <= 0;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      disabled={soldOut}
                      onClick={() => onPick(item)}
                      style={categoryColorVars(colorFor(colors, item.categoryId))}
                      className={clsx(
                        'cat-card h-10 rounded-full border px-4 text-sm transition',
                        soldOut
                          ? 'cursor-not-allowed text-ink-faint opacity-50'
                          : 'text-ink-soft hover:text-ink',
                      )}
                    >
                      {item.name}
                      {item.trackStock ? (
                        <span className="ml-1.5 text-[11px] text-danger">
                          {soldOut ? 'out' : item.stockQuantity}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          {visible.length === 0 ? (
            <EmptyState
              icon={<UtensilsCrossed size={30} />}
              title="Nothing here"
              message="No menu items match that search."
            />
          ) : (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-4">
              {visible.map((item) => (
                <ItemCard key={item.id} item={item} colors={colors} onPick={onPick} money={money} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
